import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import config from '../../config/config';

export default function AdvertiseForm({ mode = 'create', initial = {}, mainCompanyId, companies = [], onClose = () => {}, onCreated = () => {} }) {
  const [companyId, setCompanyId] = useState(initial.companyid || initial.company_id || 0);
  const [campaignName, setCampaignName] = useState(initial.campaign_name || '');
  const [campaignUrl, setCampaignUrl] = useState(initial.campaign_url || '');
  const [priority, setPriority] = useState(typeof initial.priority !== 'undefined' ? initial.priority : 1);
  const [imageFile, setImageFile] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null); // { width, height }
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // if editing and initial contains company, ensure it's set
    if (mode === 'edit') {
      setCompanyId(initial.companyid || initial.company_id || 0);
      setCampaignName(initial.campaign_name || '');
      setCampaignUrl(initial.campaign_url || '');
      setPriority(typeof initial.priority !== 'undefined' ? initial.priority : 1);
    }
  }, [initial, mode]);

  const validate = () => {
    const e = {};
    if (!companyId) e.companyId = 'Company is required';
    if (!campaignName || !campaignName.trim()) e.campaignName = 'Campaign name is required';
    if (!campaignUrl || !/^https?:\/\//i.test(campaignUrl)) e.campaignUrl = 'Valid URL required (http/https)';
    if (![0,1].includes(Number(priority))) e.priority = 'Priority required';
    if (mode === 'create' && !imageFile) e.imageFile = 'Image is required';
    if (imageFile) {
      const allowed = ['image/png','image/jpeg','image/gif'];
      if (!allowed.includes(imageFile.type)) e.imageFile = 'Allowed types: PNG, JPG, GIF';
      const max = 5 * 1024 * 1024; // 5MB
      if (imageFile.size > max) e.imageFile = 'Image must be <= 5MB';
      // dimension check: require width and height between 200 and 250
      if (imageDimensions) {
        if (imageDimensions.width < 200 || imageDimensions.width > 250 || imageDimensions.height < 200 || imageDimensions.height > 250) {
          e.imageFile = 'Image dimensions must be between 200–250px for both width and height';
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      if (mode === 'create') {
        const fd = new FormData();
        fd.append('companyid', companyId);
        fd.append('maincompanyid', mainCompanyId);
        fd.append('campaign_name', campaignName);
        fd.append('campaign_url', campaignUrl);
        fd.append('priority', priority);
        if (imageFile) fd.append('campaign_images', imageFile);
        await api.post('/admin/advertise', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        onCreated();
      } else {
        // edit
        const id = initial.id;
        if (imageFile) {
          const fd = new FormData();
          fd.append('companyid', companyId);
          fd.append('maincompanyid', mainCompanyId);
          fd.append('campaign_name', campaignName);
          fd.append('campaign_url', campaignUrl);
          fd.append('priority', priority);
          fd.append('campaign_images', imageFile);
          await api.patch(`/admin/advertise/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          // metadata only
          await api.patch(`/admin/advertise/${id}`, { companyid: companyId, maincompanyid: mainCompanyId, campaign_name: campaignName, campaign_url: campaignUrl, priority });
        }
        onCreated();
      }
    } catch (err) {
      console.error('Save failed', err?.response?.data || err.message);
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Save failed';
      setErrors({ form: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {errors.form && <div className="text-red-600">{errors.form}</div>}
      <div>
        <label className="block text-sm">Company</label>
        <select value={companyId} onChange={(e) => setCompanyId(Number(e.target.value) || 0)} className="w-full border rounded px-2 py-1 bg-white text-black">
          <option value={0}>Select company</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.company}</option>)}
        </select>
        {errors.companyId && <div className="text-red-600 text-sm">{errors.companyId}</div>}
      </div>

      <div>
        <label className="block text-sm">Campaign Name</label>
        <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} className="w-full border rounded px-2 py-1" />
        {errors.campaignName && <div className="text-red-600 text-sm">{errors.campaignName}</div>}
      </div>

      <div>
        <label className="block text-sm">Campaign URL</label>
        <input value={campaignUrl} onChange={(e) => setCampaignUrl(e.target.value)} className="w-full border rounded px-2 py-1" />
        {errors.campaignUrl && <div className="text-red-600 text-sm">{errors.campaignUrl}</div>}
      </div>

      <div>
        <label className="block text-sm">Priority</label>
        <select value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="w-full border rounded px-2 py-1 bg-white text-black">
          <option value={1}>Top</option>
          <option value={0}>Bottom</option>
        </select>
        {errors.priority && <div className="text-red-600 text-sm">{errors.priority}</div>}
      </div>

      <div>
        <label className="block text-sm">Image</label>
        <input
          type="file"
          accept="image/png, image/jpeg, image/gif"
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (!f) {
              setImageFile(null);
              setImageDimensions(null);
              return;
            }
            // basic type/size checks first
            const allowed = ['image/png','image/jpeg','image/gif'];
            const max = 5 * 1024 * 1024;
            if (!allowed.includes(f.type)) {
              setErrors((prev) => ({ ...prev, imageFile: 'Allowed types: PNG, JPG, GIF' }));
              setImageFile(null);
              setImageDimensions(null);
              return;
            }
            if (f.size > max) {
              setErrors((prev) => ({ ...prev, imageFile: 'Image must be <= 5MB' }));
              setImageFile(null);
              setImageDimensions(null);
              return;
            }
            // load image to validate dimensions
            const url = URL.createObjectURL(f);
            const img = new Image();
            img.onload = () => {
              const w = img.naturalWidth || img.width;
              const h = img.naturalHeight || img.height;
              URL.revokeObjectURL(url);
              setImageDimensions({ width: w, height: h });
              if (w < 200 || w > 250 || h < 200 || h > 250) {
                setErrors((prev) => ({ ...prev, imageFile: 'Image dimensions must be between 200–250px for both width and height' }));
                setImageFile(null);
              } else {
                setErrors((prev) => { const p = { ...prev }; delete p.imageFile; return p; });
                setImageFile(f);
              }
            };
            img.onerror = () => {
              URL.revokeObjectURL(url);
              setErrors((prev) => ({ ...prev, imageFile: 'Invalid image file' }));
              setImageFile(null);
              setImageDimensions(null);
            };
            img.src = url;
          }}
        />
  <div className="text-xs text-gray-500 mt-1">Only JPEG, PNG, GIF images. Recommended size: 200–250 x 200–250 pixels. Max size: 5MB.</div>
        {errors.imageFile && <div className="text-red-600 text-sm">{errors.imageFile}</div>}
        {mode === 'edit' && initial.campaign_images && (
          <div className="mt-2">
            <img
              src={initial.campaign_images.startsWith('/uploads') ? `${config.assetsBaseUrl}${initial.campaign_images}` : `${config.assetsBaseUrl}/uploads/advertise/${initial.campaign_images}`}
              alt="current"
              className="w-48"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
        <button type="submit" disabled={saving} className="px-3 py-1 bg-green-600 text-white rounded">{saving ? 'Saving...' : (mode === 'create' ? 'Create' : 'Save')}</button>
      </div>
    </form>
  );
}
